import React, { useEffect, useRef, useState } from 'react'
import { Card, Form, Button, Row, Col, DatePicker, Spin, Table } from 'antd'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { wtApiCall, wtApiPostWithContentTypeHandling } from 'src/helpers/commons/ApiWrapper'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtFormatMoney, wtRoundValue } from 'src/helpers/commons/utils'
import WidgetWarehouseSelector from 'src/widgets/Selectors/WidgetWarehouseSelector'
import BaseListing from 'src/BaseListings'
import { useDownloadExcel } from 'react-export-table-to-excel'
import WidgetItemSelector from 'src/widgets/Selectors/WidgetItemSelector'
import WidgetWarehouseLocationSelector from 'src/widgets/Selectors/WidgetWarehouseLocationSelector'

const StockMovementSummaryReport = () => {
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState(false)
  const [dateRange, setDateRange] = useState([])
  const [stockData, setStockData] = useState([])
  const [warehouses, setWarehouses] = useState([])
  const [WarehouseLocations, setWarehouseLocations] = useState([])
  const [Items, setItems] = useState([])
  const [WarehouseName, setWarehouseName] = useState('')
  const [WarehouseId, setWarehouseId] = useState(0)
  const [ItemName, setItemName] = useState('')
  const [ItemId, setItemId] = useState(0)
  const [WarehouseLocationId, setWarehouseLocationId] = useState(0)
  const [WarehouseLocationName, setWarehouseLocationName] = useState('')

  const Base = new BaseListing('stock_report')
  const tableRef = useRef(null)
  const companyName = wtGetFromLocalStorage('company_name')

  const changeValues = (key, val) => {
    if (key === 'warehouse_id') {
      setWarehouseId(val.value)
      setWarehouseName(val.label)
    }
    if (key === 'item_id') {
      setItemId(val.value)
      setItemName(val.label)
    }
    if (key === 'warehouse_location_id') {
      setWarehouseLocationId(val.value)
      setWarehouseLocationName(val.label)
    }
  }


  const columns = [
    {
      title: 'Warehouse',
      dataIndex: ['warehouse', 'name'],
      key: 'warehouse',
      width: 120,
      render: (val, record) => record?.warehouse?.name ?? '',
    },
    {
      title: 'Item Code',
      dataIndex: ['item', 'code'],
      key: 'item_code',
      width: 100,
      render: (val, record) => record?.item?.code ?? '',
    },
    {
      title: 'Item Name',
      dataIndex: ['item', 'name'],
      key: 'item_name',
      width: 180,
      render: (val, record) => record?.item?.name ?? '',
    },
    {
      title: 'Brand',
      dataIndex: ['item_brand', 'name'],
      key: 'brand_name',
      width: 120,
      render: (val, record) => record?.item_brand?.name ?? '',
    },
    {
      title: 'UOM',
      dataIndex: ['item_unit', 'name'],
      key: 'unit_name',
      width: 80,
      render: (val, record) => record?.item_unit?.name ?? '',
    },
    {
      title: 'Branch',
      dataIndex: ['branch', 'name'],
      key: 'branch',
      width: 120,
      render: (val, record) => record?.branch?.name ?? '',
    },
    {
      title: 'Opening Qty',
      dataIndex: 'opening_qty',
      key: 'opening_qty',
      align: 'right',
      width: 110,
      render: (value) => (
        <span style={{ fontWeight: 600, color: '#1890ff' }}>
          {wtRoundValue(value ?? 0, 0)}
        </span>
      ),
    },
    {
      title: 'Total GRN',
      dataIndex: 'total_grn',
      key: 'total_grn',
      align: 'right',
      width: 100,
      render: (value) => wtRoundValue(value ?? 0, 0),
    },
    {
      title: 'Transfer IN',
      dataIndex: 'total_transfer_in',
      key: 'total_transfer_in',
      align: 'right',
      width: 110,
      render: (value) => (
        <span style={{ color: '#52c41a' }}>
          {wtRoundValue(value ?? 0, 0)}
        </span>
      ),
    },
    {
      title: 'Transfer OUT',
      dataIndex: 'total_transfer_out',
      key: 'total_transfer_out',
      align: 'right',
      width: 110,
      render: (value) => (
        <span style={{ color: '#fa8c16' }}>
          {wtRoundValue(value ?? 0, 0)}
        </span>
      ),
    },
    {
      title: 'Issued QTY',
      dataIndex: 'total_issued',
      key: 'total_issued',
      align: 'right',
      width: 100,
      render: (value) => (
        <span style={{ color: '#ff4d4f' }}>
          {wtRoundValue(value ?? 0, 0)}
        </span>
      ),
    },
    {
      title: 'Closing Qty',
      dataIndex: 'closing_qty',
      key: 'closing_qty',
      align: 'right',
      width: 110,
      render: (value) => (
        <span style={{ fontWeight: 700, color: '#722ed1' }}>
          {wtRoundValue(value ?? 0, 0)}
        </span>
      ),
    },
  ]

  const rowClassName = (record) => {
    if (record?.item?.name === 'TOTAL') return 'total-row'
    return ''
  }

  const getReportData = () => {
    if (!dateRange || dateRange.length !== 2) return

    setIsLoading(true)

    wtApiCall(
      'reports/stock_movement_summary_report',
      {
        from_date: dateRange[0].format('YYYY-MM-DD'),
        to_date: dateRange[1].format('YYYY-MM-DD'),
        warehouse_id: WarehouseId,
        item_id: ItemId,
        warehouse_location_id: WarehouseLocationId,
      },
      'get',
      (response) => {
        if (response?.data) {
          setStockData(response.data.data || [])
        } else {
          setStockData([])
        }
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      }
    )
  }

  const { onDownload } = useDownloadExcel({
    currentTableRef: tableRef.current,
    filename: 'Stock Movement Summary Report',
    sheet: 'Sheet',
  })

  useEffect(() => {
    Base.dropdownData(
      'warehouses,items,warehouse_locations',
      {},
      [setWarehouses, setItems, setWarehouseLocations],
      1
    )
  }, [])

  return (
    <div
      className="container-fluid"
      style={{
        padding: '1em',
        backgroundColor: 'white',
        minHeight: '100vh',
        fontFamily: 'Helvetica',
      }}
    >
      {/* Total row bold styling */}
      <style>{`
        .total-row td {
          background-color: #f0f0f0 !important;
          font-weight: 700 !important;
          font-size: 13px;
        }
      `}</style>

      <Card
        title={
          <span style={{ fontSize: '22px', textAlign: 'center', display: 'block' }}>
            {companyName + ' — Stock Movement Summary Report'}
          </span>
        }
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12} md={4}>
            <label>Date Range *</label>
            <DatePicker.RangePicker
              value={dateRange}
              onChange={(value) => setDateRange(value)}
              style={{ width: '100%', height: 35 }}
            />
          </Col>

          <Col xs={24} sm={12} md={4}>
            <label>Warehouse</label>
            <WidgetWarehouseSelector
              warehouses={warehouses}
              onChange={(values) => changeValues('warehouse_id', values)}
              displayValue={WarehouseName}
              reloadDropdownData={() =>
                Base.dropdownData('warehouses', {}, [setWarehouses], 1)
              }
              value={WarehouseId}
            />
          </Col>

          <Col xs={24} sm={12} md={4}>
            <label>Item</label>
            <WidgetItemSelector
              items={Items}
              onChange={(values) => changeValues('item_id', values)}
              displayValue={ItemName}
              reloadDropdownData={() =>
                Base.dropdownData('items', {}, [setItems], 1)
              }
              placeHolder="Select Item"
              value={ItemId}
            />
          </Col>

          <Col xs={24} sm={12} md={4}>
            <label>Warehouse Location</label>
            <WidgetWarehouseLocationSelector
              warehouse_locations={WarehouseLocations}
              onChange={(values) => changeValues('warehouse_location_id', values)}
              displayValue={WarehouseLocationName}
              reloadDropdownData={() =>
                Base.dropdownData('warehouse_locations', {}, [setWarehouseLocations], 1)
              }
              value={WarehouseLocationId}
            />
          </Col>

          <Col xs={24} md={5}>
            <Button
              type="primary"
              onClick={getReportData}
              disabled={isLoading}
              style={{ marginRight: 6, marginTop: 29 }}
            >
              Generate
            </Button>

            <Button
              style={{ backgroundColor: 'green', color: '#fff', marginRight: 10 }}
              onClick={onDownload}
            >
              Export
            </Button>

          </Col>
        </Row>

        <br />

        <div ref={tableRef}>
          <Table
            columns={columns}
            dataSource={stockData}
            pagination={false}
            bordered
            size="small"
            rowKey={(record, index) => index}
            rowClassName={rowClassName}
            loading={{ indicator: <Spin />, spinning: isLoading }}
            // scroll={{ x: 'max-content' }}
          />
        </div>
      </Card>
    </div>
  )
}

export default StockMovementSummaryReport